import { useEffect, useMemo, useRef, useState } from "react";
import {
  Crosshair,
  MapPin,
  Radar,
  Loader2,
  ExternalLink,
  Building2,
  Plus,
  CheckCircle2,
  AlertTriangle,
  Info,
  Activity,
  List,
  Map,
  Target,
  Navigation,
  Route,
  Users,
  Sparkles,
  Phone,
  X,
  ChevronUp,
  ChevronDown,
  Save,
  GripVertical,
  CircleDollarSign,
  BriefcaseBusiness,
  StopCircle,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import ActionLogDialog from "../actions/ActionLogDialog";

import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const CATS = [
  ["promoteurs", "Promoteurs immobiliers"],
  ["constructeurs", "Constructeurs"],
  ["entreprises_batiment", "Entreprises générales du bâtiment"],
  ["renovation", "Entreprises de rénovation"],
  ["artisans", "Artisans"],
  ["architectes", "Architectes"],
  ["moe", "Maîtres d'œuvre"],
  ["agences_immo", "Agences immobilières"],
  ["syndics", "Syndics / gestion de biens"],
  ["investisseurs", "Investisseurs / marchands de biens"],
  ["decorateurs", "Décorateurs / archi d'intérieur"],
];

const PRIO = {
  HAUTE: {
    l: "PRIORITÉ HAUTE",
    c: "bg-red-500/10 text-red-400 border-red-500/20",
  },
  INTERESSANT: {
    l: "INTÉRESSANT",
    c: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  },
  A_SURVEILLER: {
    l: "À SURVEILLER",
    c: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  },
  FAIBLE: {
    l: "FAIBLE",
    c: "bg-slate-500/10 text-slate-300 border-slate-500/30",
  },
};

const SCORE_LABELS = {
  potentiel_ca: ["Potentiel CA", 25],
  volume: ["Volume projets", 20],
  proximite: ["Proximité", 15],
  adequation: ["Adéquation familles", 15],
  activite_recente: ["Activité récente", 10],
  recurrence: ["Récurrence / capacité", 10],
  contactabilite: ["Contactabilité", 5],
};

const STATUTS = [
  "Nouveau",
  "Contacté",
  "Échange",
  "Opportunité",
  "Projet",
  "Gagné",
  "Perdu",
];

const STORE_CENTER = [44.2803, 4.7492];

const markerCache = {};

function markerIcon(priority = "FAIBLE") {
  if (markerCache[priority]) return markerCache[priority];

  const colors = {
    HAUTE: "#ef4444",
    INTERESSANT: "#f59e0b",
    A_SURVEILLER: "#38bdf8",
    FAIBLE: "#94a3b8",
  };

  const color = colors[priority] || colors.FAIBLE;

  markerCache[priority] = L.divIcon({
    className: "hunter-map-marker",
    html: `
      <div style="
        width: 28px;
        height: 28px;
        border-radius: 999px;
        background: ${color};
        border: 3px solid white;
        box-shadow: 0 3px 12px rgba(15,23,42,.35);
        display:flex;
        align-items:center;
        justify-content:center;
        color:white;
        font-weight:800;
        font-size:12px;
      ">●</div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });

  return markerCache[priority];
}

export default function BusinessHunter() {
  const [familles, setFamilles] = useState([]);
  const [params, setParams] = useState({
    radius_km: 30,
    categories: [
      "promoteurs",
      "agences_immo",
      "renovation",
      "constructeurs",
    ],
    familles_prioritaires: [],
    freshness_months: 36,
    max_results: 30,
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [sel, setSel] = useState(null);
  const [added, setAdded] = useState({});
  const [actionOpen, setActionOpen] = useState(false);
  const [actionCtx, setActionCtx] = useState(null);
  const [viewMode, setViewMode] = useState("map");
  const [mapFilter, setMapFilter] = useState("ALL");

  const [tourItems, setTourItems] = useState([]);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourId, setTourId] = useState(null);
  const [tourStatus, setTourStatus] = useState("Planifiée");
  const [tourName, setTourName] = useState("Tournée commerciale");
  const [tourDate, setTourDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [savingTour, setSavingTour] = useState(false);
  const [updatingTourStatus, setUpdatingTourStatus] = useState(false);

  const [contactOpen, setContactOpen] = useState(false);
  const [contactCanal, setContactCanal] = useState("Téléphone");
  const [contactResultat, setContactResultat] = useState("");
  const [contactCommentaire, setContactCommentaire] = useState("");
  const [contactNextDate, setContactNextDate] = useState("");
  const [contactNextAction, setContactNextAction] = useState("");
  const [savingContact, setSavingContact] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);
  const [webEnriching, setWebEnriching] = useState(false);
  const [webEnrichResult, setWebEnrichResult] = useState(null);

  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [bulkStopRequested, setBulkStopRequested] = useState(false);
  const bulkStopRef = useRef(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [bulkStats, setBulkStats] = useState({
    direct: 0,
    indirect: 0,
    none: 0,
    errors: 0,
  });

  const [opportunityOpen, setOpportunityOpen] = useState(false);
  const [creatingOpportunity, setCreatingOpportunity] = useState(false);
  const [createdOpportunity, setCreatedOpportunity] = useState(null);
  const [existingOpportunity, setExistingOpportunity] = useState(null);
  const [loadingOpportunity, setLoadingOpportunity] = useState(false);
  const [opportunityForm, setOpportunityForm] = useState({
    titre: "",
    famille_principale: "",
    valeur_ht: "",
    marge_estimee_ht: "",
    prochaine_action: "",
    prochaine_date: "",
    echeance: "",
    priorite: "Normale",
    commercial: "",
  });

  useEffect(() => {
    api
      .get("/familles")
      .then((r) => setFamilles(r.data.map((f) => f.nom)))
      .catch(() => {});

    const paramsUrl = new URLSearchParams(window.location.search);
    const requestedTourId = paramsUrl.get("tour");

    if (!requestedTourId) return;

    const loadRequestedTour = async () => {
      try {
        const response = await api.get(
          `/hunter/tours/${requestedTourId}`
        );

        const tour = response?.data?.tour;

        if (!tour) {
          toast.error("Tournée introuvable");
          return;
        }

        setTourId(tour.id);
        setTourName(tour.nom || "Tournée commerciale");
        setTourDate(tour.date || new Date().toISOString().slice(0, 10));
        setTourStatus(tour.statut || "Planifiée");

        if (Array.isArray(tour.etapes)) {
          setTourItems(
            tour.etapes.map((etape) => ({
              ...etape,
              siren: etape.siren || "",
              nom: etape.nom || "",
              categorie: etape.categorie || "",
              categorie_label: etape.categorie_label || "",
            }))
          );
        }

        setTourOpen(true);

        // Nettoie l'URL après ouverture pour éviter de recharger
        // automatiquement la même tournée lors d'une navigation ultérieure.
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete("tour");
        window.history.replaceState({}, "", cleanUrl.toString());
      } catch (e) {
        toast.error(
          formatApiErrorDetail(e?.response?.data?.detail) ||
            "Impossible de charger la tournée"
        );
      }
    };

    loadRequestedTour();
  }, []);

  const toggle = (key, val) =>
    setParams((p) => ({
      ...p,
      [key]: p[key].includes(val)
        ? p[key].filter((x) => x !== val)
        : [...p[key], val],
    }));

  const launch = async () => {
    setLoading(true);
    setResult(null);

    try {
      const { data } = await api.post("/hunter/search", params);
      setResult(data);

      if (data.count === 0) {
        toast.info("Aucun prospect trouvé dans cette zone/critères.");
      } else {
        toast.success(`${data.count} prospect(s) détecté(s)`);
      }

      if (data.incomplete) {
        toast.warning(
          "Certaines catégories n'ont pas pu être interrogées (résultats incomplets)."
        );
      }
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Recherche indisponible"
      );
    } finally {
      setLoading(false);
    }
  };

  const addToCrm = async (p, force = false) => {
    try {
      const { data } = await api.post("/hunter/prospects", {
        nom: p.nom,
        categorie: p.categorie,
        categorie_label: p.categorie_label,
        ville: p.ville,
        code_postal: p.code_postal,
        adresse:
          p.adresse && typeof p.adresse === "object"
            ? p.adresse
            : null,
        latitude: p.latitude,
        longitude: p.longitude,
        distance_km: p.distance_km,
        siren: p.siren,
        source_info: p.source_info,
        signal: p.signal,
        signal_niveau: p.signal_niveau,
        signal_date: p.signal_date,
        score: p.score,
        score_detail:
          p.score_detail && typeof p.score_detail === "object"
            ? p.score_detail
            : null,
        priorite: p.priorite,
        niveau_cible: p.niveau_cible,
        raisons_priorite: p.raisons_priorite,
        signaux_commerciaux: p.signaux_commerciaux,
        profil_cible: p.profil_cible,
        potentiel_niveau: p.potentiel_niveau,
        potentiel_raisons: p.potentiel_raisons,
        familles: p.familles,
        familles_compatibles: p.familles_compatibles,
        offres_compatibles: p.offres_compatibles,
        orientation: p.orientation,
        orientation_label: p.orientation_label,
        score_strategique: p.score_strategique,
        argument_commercial: p.argument_commercial,
        action_recommandee:
          p.action_recommandee &&
          typeof p.action_recommandee === "object"
            ? p.action_recommandee
            : null,
        justification: p.justification,
        nombre_etablissements: p.nombre_etablissements,
        date_mise_a_jour: p.date_mise_a_jour,
        chasse_id: result?.chasse_id,
        force,
      });

      if (data.status === "existant") {
        toast.info("Entreprise déjà présente dans le CRM.");
        setAdded((a) => ({ ...a, [p.siren]: data.client.id }));
      } else {
        toast.success("Prospect ajouté au CRM");
        setAdded((a) => ({ ...a, [p.siren]: data.client.id }));
        window.dispatchEvent(new CustomEvent("crm:data-changed"));
      }
      return data.client.id;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      return null;
    }
  };

  const logAction = (p) => {
    const cid = added[p.siren] || p.existing_ref?.id;

    if (!cid) {
      return toast.info("Ajoutez d'abord le prospect au CRM.");
    }

    setActionCtx({
      client_id: cid,
      client_nom: p.nom,
      preset_type: "appel",
      reco_categorie: "business_hunter",
    });

    setActionOpen(true);
  };

  const inCrm = (p) => added[p.siren] || p.dedup_status === "existant";

  const mapProspects = useMemo(() => {
    if (!result?.prospects) return [];

    return result.prospects.filter((p) => {
      if (mapFilter === "ALL") return true;
      return p.priorite === mapFilter;
    });
  }, [result, mapFilter]);

  const localisedProspects = useMemo(
    () =>
      result?.prospects?.filter(
        (p) =>
          p.latitude != null &&
          p.longitude != null &&
          p.distance_km != null
      ) || [],
    [result]
  );

  const isInTour = (p) =>
    tourItems.some((item) => item.siren === p.siren);

  const toggleTour = (p) => {
    setTourItems((items) => {
      if (items.some((item) => item.siren === p.siren)) {
        return items.filter((item) => item.siren !== p.siren);
      }

      return [...items, p];
    });
  };

  const moveTourItem = (index, direction) => {
    setTourItems((items) => {
      const next = [...items];
      const target = index + direction;

      if (target < 0 || target >= next.length) {
        return items;
      }

      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeTourItem = (siren) => {
    setTourItems((items) =>
      items.filter((item) => item.siren !== siren)
    );
  };

  const haversineKm = (a, b) => {
    if (
      a?.latitude == null ||
      a?.longitude == null ||
      b?.latitude == null ||
      b?.longitude == null
    ) {
      return 0;
    }

    const R = 6371;
    const lat1 = Number(a.latitude) * Math.PI / 180;
    const lat2 = Number(b.latitude) * Math.PI / 180;
    const dLat = lat2 - lat1;
    const dLon =
      (Number(b.longitude) - Number(a.longitude)) *
      Math.PI / 180;

    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1) *
        Math.cos(lat2) *
        Math.sin(dLon / 2) ** 2;

    return 2 * R * Math.asin(Math.sqrt(h));
  };

  const tourDistance = useMemo(() => {
    if (!tourItems.length) return 0;

    let total = 0;
    let previous = {
      latitude: STORE_CENTER[0],
      longitude: STORE_CENTER[1],
    };

    for (const item of tourItems) {
      total += haversineKm(previous, item);
      previous = item;
    }

    return Math.round(total * 10) / 10;
  }, [tourItems]);

  const ensureProspectInCrm = async (p) => {
    const existingId =
      added[p.siren] ||
      p.existing_ref?.id ||
      p.client_id ||
      p.id;

    if (existingId) {
      return existingId;
    }

    if (p.dedup_status === "doublon_probable") {
      toast.warning(
        "Doublon potentiel : ajoutez d'abord le prospect au CRM."
      );
      return null;
    }

    return await addToCrm(p, false);
  };

  const enrichProspect = async () => {
    const clientId =
      added[sel?.siren] ||
      sel?.existing_ref?.id ||
      sel?.client_id ||
      sel?.id;

    if (!clientId) {
      toast.warning("Ajoute d'abord ce prospect au CRM.");
      return;
    }

    setEnriching(true);
    setEnrichResult(null);

    try {
      const { data } = await api.post(
        `/hunter/prospects/${clientId}/enrichir`,
        { force: true }
      );

      const updated = data?.client || null;

      if (updated) {
        setSel((current) => {
          if (!current) return current;
          return {
            ...current,
            ...updated,
            id: updated.id || current.id,
            client_id: updated.id || current.client_id,
            telephone: updated.telephone || "",
            email: updated.email || "",
            site_web: updated.site_web || "",
            hunter: updated.hunter || current.hunter,
            prospection: updated.prospection || current.prospection,
          };
        });

        if (updated.id) {
          setAdded((current) => ({
            ...current,
            [sel.siren]: updated.id,
          }));
        }
      }

      setEnrichResult(data);

      if (data.status === "coordonnees_trouvees") {
        toast.success("Coordonnées publiques trouvées.");
      } else {
        toast.info("Aucune coordonnée publique trouvée.");
      }
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Enrichissement indisponible"
      );
    } finally {
      setEnriching(false);
    }
  };

  const enrichProspectWeb = async () => {
    const clientId = added[sel?.siren] || sel?.existing_ref?.id || sel?.client_id || sel?.id;
    if (!clientId) { toast.warning("Ajoute d'abord ce prospect au CRM."); return; }
    setWebEnriching(true); setWebEnrichResult(null);
    try {
      const { data } = await api.post(`/hunter/prospects/${clientId}/enrichir-web`, { force: true });
      setWebEnrichResult(data);
      if (data?.client) {
        // Le backend renvoie le prospect CRM avec les données Hunter sous
        // `client.hunter`. On conserve donc le résultat Hunter affiché
        // (score, score_detail, priorité, angle commercial, etc.) au lieu
        // de remplacer `sel` par le document CRM brut.
        setSel((current) => {
          const updated = data.client;
          const hunter = updated.hunter || current?.hunter || {};
          return {
            ...(current || {}),
            ...updated,
            id: updated.id || current?.id,
            client_id: updated.id || current?.client_id,
            hunter,
            score: current?.score ?? hunter.score ?? updated.score,
            score_detail: current?.score_detail || hunter.score_detail || updated.score_detail,
            priorite: current?.priorite || hunter.priorite || updated.priorite,
            niveau_cible: current?.niveau_cible || hunter.niveau_cible || updated.niveau_cible,
            raisons_priorite: current?.raisons_priorite || hunter.raisons_priorite || updated.raisons_priorite,
            signaux_commerciaux: current?.signaux_commerciaux || hunter.signaux_commerciaux || updated.signaux_commerciaux,
            profil_cible: current?.profil_cible || hunter.profil_cible || updated.profil_cible,
            potentiel_niveau: current?.potentiel_niveau || hunter.potentiel_niveau || updated.potentiel_niveau,
            potentiel_raisons: current?.potentiel_raisons || hunter.potentiel_raisons || updated.potentiel_raisons,
            familles: current?.familles || hunter.familles || updated.familles,
            familles_compatibles: current?.familles_compatibles || hunter.familles_compatibles || updated.familles_compatibles,
            offres_compatibles: current?.offres_compatibles || hunter.offres_compatibles || updated.offres_compatibles,
            orientation: current?.orientation || hunter.orientation || updated.orientation,
            orientation_label: current?.orientation_label || hunter.orientation_label || updated.orientation_label,
            score_strategique: current?.score_strategique ?? hunter.score_strategique ?? updated.score_strategique,
            argument_commercial: current?.argument_commercial || hunter.argument_commercial || updated.argument_commercial,
            action_recommandee: current?.action_recommandee || hunter.action_recommandee || updated.action_recommandee,
            justification: current?.justification || hunter.justification || updated.justification,
          };
        });
      }
      if (data?.status === "contact_direct") toast.success("Contact Web trouvé et vérifié.");
      else if (data?.status === "contact_indirect") toast.success("Site Web trouvé : contact indirect disponible.");
      else if (data?.status === "provider_non_configure") toast.info("Recherche Web non configurée sur TEST.");
      else toast.info("Aucun contact Web suffisamment concordant trouvé.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Recherche Web indisponible");
    } finally { setWebEnriching(false); }
  };

  const enrichWholeHunt = async () => {
    const prospects = result?.prospects || [];

    if (!prospects.length) {
      toast.info("Lancez d'abord une chasse.");
      return;
    }

    if (bulkEnriching) return;

    setBulkEnriching(true);
    setBulkStopRequested(false);
    bulkStopRef.current = false;
    setBulkProgress({ done: 0, total: prospects.length });
    setBulkStats({ direct: 0, indirect: 0, none: 0, errors: 0 });

    let direct = 0;
    let indirect = 0;
    let none = 0;
    let errors = 0;
    let stopped = false;

    try {
      for (let i = 0; i < prospects.length; i += 1) {
        if (bulkStopRef.current) {
          stopped = true;
          break;
        }

        const prospect = prospects[i];

        try {
          let clientId =
            added[prospect.siren] ||
            prospect.existing_ref?.id ||
            prospect.client_id ||
            null;

          if (!clientId) {
            clientId = await addToCrm(prospect);
          }

          if (!clientId) {
            errors += 1;
          } else {
            const { data } = await api.post(
              `/hunter/prospects/${clientId}/enrichir-web`,
              { force: true }
            );

            // Réinjecte immédiatement les coordonnées Web dans le résultat
            // Hunter et dans la fiche actuellement sélectionnée.
            if (data?.client) {
              const updatedClient = data.client;

              setResult((currentResult) => {
                if (!currentResult?.prospects) return currentResult;

                return {
                  ...currentResult,
                  prospects: currentResult.prospects.map((item) =>
                    item.siren === prospect.siren
                      ? {
                          ...item,
                          telephone:
                            updatedClient.telephone || item.telephone,
                          email:
                            updatedClient.email || item.email,
                          site_web:
                            updatedClient.site_web || item.site_web,
                          site:
                            updatedClient.site_web || item.site,
                          client_id: updatedClient.id || item.client_id,
                          existing_ref: {
                            ...(item.existing_ref || {}),
                            id: updatedClient.id || item.existing_ref?.id,
                          },
                        }
                      : item
                  ),
                };
              });

              setSel((currentSel) => {
                if (!currentSel || currentSel.siren !== prospect.siren) {
                  return currentSel;
                }

                return {
                  ...currentSel,
                  ...updatedClient,
                  siren: currentSel.siren,
                  telephone:
                    updatedClient.telephone || currentSel.telephone,
                  email:
                    updatedClient.email || currentSel.email,
                  site_web:
                    updatedClient.site_web || currentSel.site_web,
                  site:
                    updatedClient.site_web || currentSel.site,
                };
              });
            }

            if (data?.status === "contact_direct") {
              direct += 1;
            } else if (data?.status === "contact_indirect") {
              indirect += 1;
            } else if (data?.status === "non_contactable") {
              none += 1;
            } else if (data?.status === "provider_non_configure") {
              errors += 1;
              toast.error("Recherche Web non configurée sur TEST.");
              stopped = true;
              break;
            } else {
              none += 1;
            }
          }
        } catch (e) {
          errors += 1;
        }

        setBulkStats({ direct, indirect, none, errors });
        setBulkProgress({ done: i + 1, total: prospects.length });
      }
    } finally {
      const wasStopped = stopped || bulkStopRef.current;

      setBulkEnriching(false);
      setBulkStopRequested(false);
      bulkStopRef.current = false;

      if (wasStopped) {
        toast.info(
          `Enrichissement arrêté : ${direct} direct, ${indirect} indirect, ${none} sans contact, ${errors} erreur(s).`
        );
      } else {
        toast.success(
          `Enrichissement terminé : ${direct} direct, ${indirect} indirect, ${none} sans contact, ${errors} erreur(s).`
        );
      }
    }
  };


  const loadExistingOpportunity = async (prospect) => {
    if (!prospect) return;

    const clientId =
      added[prospect.siren] ||
      prospect.existing_ref?.id ||
      prospect.client_id ||
      prospect.id;

    if (!clientId) {
      setExistingOpportunity(null);
      return;
    }

    setLoadingOpportunity(true);

    try {
      const { data } = await api.get("/opportunities");

      const opportunities = Array.isArray(data)
        ? data
        : data?.opportunities || data?.items || [];

      const openOpportunity =
        opportunities.find(
          (opp) =>
            opp.client_id === clientId &&
            !["Signé", "Perdu"].includes(opp.etape) &&
            opp.is_demo !== true
        ) || null;

      setExistingOpportunity(openOpportunity);
    } catch (e) {
      setExistingOpportunity(null);
    } finally {
      setLoadingOpportunity(false);
    }
  };

  const openOpportunityDialog = () => {
    if (!sel) return;

    const famillesCompatibles =
      sel.familles_compatibles ||
      sel.hunter?.familles_compatibles ||
      sel.familles ||
      sel.hunter?.familles ||
      [];

    const famille =
      famillesCompatibles?.[0] ||
      sel.offres_compatibles?.[0] ||
      sel.hunter?.offres_compatibles?.[0] ||
      "";

    const priorite =
      sel.priorite === "HAUTE"
        ? "Haute"
        : sel.priorite === "INTERESSANT"
          ? "Normale"
          : "Normale";

    setCreatedOpportunity(null);
    setOpportunityForm({
      titre: `Opportunité — ${sel.nom || "Prospect"}`,
      famille_principale: famille,
      valeur_ht: "",
      marge_estimee_ht: "",
      prochaine_action:
        sel.action_recommandee?.label ||
        sel.hunter?.action_recommandee?.label ||
        "Prendre contact",
      prochaine_date: "",
      echeance: "",
      priorite,
      commercial: "",
    });
    setOpportunityOpen(true);
  };

  const createOpportunity = async () => {
    if (!sel) return;

    setCreatingOpportunity(true);

    try {
      const cid = await ensureProspectInCrm(sel);

      if (!cid) return;

      const { data } = await api.post(
        `/hunter/prospects/${cid}/opportunity`,
        {
          titre: opportunityForm.titre.trim(),
          famille_principale: opportunityForm.famille_principale,
          valeur_ht: Number(opportunityForm.valeur_ht || 0),
          marge_estimee_ht: Number(opportunityForm.marge_estimee_ht || 0),
          prochaine_action: opportunityForm.prochaine_action.trim(),
          prochaine_date: opportunityForm.prochaine_date,
          echeance: opportunityForm.echeance,
          priorite: opportunityForm.priorite,
          commercial: opportunityForm.commercial.trim(),
        }
      );

      if (data.status === "existant") {
        setCreatedOpportunity(data.opportunity || null);
        toast.info("Une opportunité ouverte existe déjà pour ce prospect.");
      } else {
        setCreatedOpportunity(data.opportunity || null);
        toast.success("Opportunité créée dans le Pipeline");
        window.dispatchEvent(new CustomEvent("crm:data-changed"));
      }
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Impossible de créer l'opportunité"
      );
    } finally {
      setCreatingOpportunity(false);
    }
  };

  useEffect(() => {
    if (!sel) {
      setExistingOpportunity(null);
      setCreatedOpportunity(null);
      return;
    }

    setCreatedOpportunity(null);
    loadExistingOpportunity(sel);
  }, [sel?.id, sel?.siren]);

  const saveContact = async () => {
    if (!sel) return;

    if (!contactResultat) {
      toast.error("Choisissez le résultat du contact.");
      return;
    }

    if (
      ["Demande de rappel", "RDV prévu"].includes(contactResultat) &&
      (!contactNextDate || !contactNextAction.trim())
    ) {
      toast.error(
        "Pour un rappel ou un rendez-vous, indiquez une date et une prochaine action."
      );
      return;
    }

    setSavingContact(true);

    try {
      const cid = await ensureProspectInCrm(sel);
      if (!cid) return;

      const response = await api.post(
        `/hunter/prospects/${cid}/contact`,
        {
          canal: contactCanal,
          resultat: contactResultat,
          commentaire: contactCommentaire.trim(),
          prochaine_date: contactNextDate,
          prochaine_action: contactNextAction.trim(),
        }
      );

      const updated = response?.data?.client;

      if (updated) {
        setSel((current) => ({
          ...current,
          ...updated,
          ...(updated.prospection || {}),
        }));
      }

      toast.success("Contact enregistré dans le CRM");
      setContactOpen(false);

      setContactResultat("");
      setContactCommentaire("");
      setContactNextDate("");
      setContactNextAction("");

      window.dispatchEvent(new CustomEvent("crm:data-changed"));
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Impossible d'enregistrer le contact"
      );
    } finally {
      setSavingContact(false);
    }
  };

  const saveTour = async () => {
    if (!tourItems.length) {
      toast.info("Ajoutez au moins un prospect à la tournée.");
      return;
    }

    setSavingTour(true);

    try {
      const etapes = tourItems.map((p, index) => ({
        ordre: index + 1,
        siren: p.siren,
        nom: p.nom,
        categorie: p.categorie,
        categorie_label: p.categorie_label,
        ville: p.ville,
        code_postal: p.code_postal,
        adresse: p.adresse,
        latitude: p.latitude,
        longitude: p.longitude,
        distance_km: p.distance_km,
        score: p.score,
        priorite: p.priorite,
        profil_cible: p.profil_cible,
        offres_compatibles: p.offres_compatibles,
        statut: "À visiter",
      }));

      const response = await api.post("/hunter/tours", {
        nom: tourName,
        date: tourDate,
        rayon_km: params.radius_km,
        etapes,
        notes: `Tournée préparée depuis Business Hunter — ${tourItems.length} étape(s), environ ${tourDistance} km depuis Bollène.`,
      });

      // Le backend attribue un identifiant stable à chaque étape.
      // On conserve ces IDs côté interface pour que toutes les
      // futures mises à jour utilisent l'identifiant de l'étape,
      // jamais sa position dans la liste.
      const savedTour = response?.data?.tour;

      if (savedTour?.id) {
        setTourId(savedTour.id);
      }

      if (savedTour?.statut) {
        setTourStatus(savedTour.statut);
      }

      const savedEtapes = savedTour?.etapes;
      if (Array.isArray(savedEtapes) && savedEtapes.length) {
        setTourItems(
          savedEtapes.map((etape) => ({
            ...etape,
            siren: etape.siren || "",
            nom: etape.nom || "",
            categorie: etape.categorie || "",
            categorie_label: etape.categorie_label || "",
          }))
        );
      }

      toast.success("Tournée enregistrée dans le CRM");
      setTourOpen(false);
      window.dispatchEvent(new CustomEvent("crm:data-changed"));
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Impossible d'enregistrer la tournée"
      );
    } finally {
      setSavingTour(false);
    }
  };

  const updateTourStatus = async (nextStatus) => {
    if (!tourId) {
      toast.info("Enregistrez d'abord la tournée.");
      return;
    }

    if (tourStatus === nextStatus) return;

    setUpdatingTourStatus(true);

    try {
      const response = await api.patch(
        `/hunter/tours/${tourId}/statut`,
        { statut: nextStatus }
      );

      const updatedTour = response?.data?.tour;

      setTourStatus(updatedTour?.statut || nextStatus);

      if (Array.isArray(updatedTour?.etapes)) {
        setTourItems(
          updatedTour.etapes.map((etape) => ({
            ...etape,
            siren: etape.siren || "",
            nom: etape.nom || "",
            categorie: etape.categorie || "",
            categorie_label: etape.categorie_label || "",
          }))
        );
      }

      toast.success(
        nextStatus === "En cours"
          ? "Tournée démarrée"
          : nextStatus === "Terminée"
            ? "Tournée terminée"
            : "Tournée annulée"
      );

      window.dispatchEvent(new CustomEvent("crm:data-changed"));
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Impossible de modifier le statut de la tournée"
      );
    } finally {
      setUpdatingTourStatus(false);
    }
  };

  const tourIsStarted = tourStatus === "En cours";
  const tourIsFinished =
    tourStatus === "Terminée" || tourStatus === "Annulée";
  const tourIsLocked = tourIsStarted || tourIsFinished;

  const hideFloatingTour = Boolean(
    sel || actionOpen || contactOpen || tourOpen
  );

  return (
    <div className="space-y-6" data-testid="bh-view">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Crosshair className="h-6 w-6 text-amber-400" />
            <h1 className="font-head text-3xl font-extrabold tracking-tight">
              Business Hunter
            </h1>
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">
              Terrain
            </span>
          </div>

          <p className="text-slate-400 text-sm mt-1">
            Prospection B2B locale — détecter, qualifier et transformer les
            acteurs du marché en opportunités commerciales.
          </p>
        </div>

        {result && (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 shadow-sm">
              <Radar className="h-3.5 w-3.5 text-amber-500" />
              {result.companies_scanned || 0} analysées
            </span>

            <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 shadow-sm">
              <MapPin className="h-3.5 w-3.5 text-sky-500" />
              {result.localised_count || 0} localisées
            </span>

            <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 shadow-sm">
              <Target className="h-3.5 w-3.5 text-red-500" />
              {result.count || 0} cibles
            </span>
          </div>
        )}
      </div>

      {/* Paramètres */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 space-y-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-1">
            <Label className="text-slate-500 text-xs">Rayon (km)</Label>
            <Input
              data-testid="bh-radius"
              type="number"
              value={params.radius_km}
              onChange={(e) =>
                setParams({
                  ...params,
                  radius_km: Number(e.target.value),
                })
              }
              className="bg-slate-50 border-slate-200 font-mono-metric"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-slate-500 text-xs">
              Fraîcheur signaux (mois)
            </Label>
            <Input
              data-testid="bh-freshness"
              type="number"
              value={params.freshness_months}
              onChange={(e) =>
                setParams({
                  ...params,
                  freshness_months: Number(e.target.value),
                })
              }
              className="bg-slate-50 border-slate-200 font-mono-metric"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-slate-500 text-xs">Résultats max</Label>
            <Input
              data-testid="bh-max"
              type="number"
              value={params.max_results}
              onChange={(e) =>
                setParams({
                  ...params,
                  max_results: Number(e.target.value),
                })
              }
              className="bg-slate-50 border-slate-200 font-mono-metric"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-slate-500 text-xs">Zone</Label>
            <div className="h-10 flex items-center gap-1.5 px-3 rounded-md bg-slate-50 border border-slate-200 text-sm text-slate-600">
              <MapPin className="h-3.5 w-3.5 text-amber-500" />
              Bollène (84)
            </div>
          </div>
        </div>

        <div>
          <Label className="text-slate-500 text-xs">
            Catégories de prospects
          </Label>

          <div className="flex flex-wrap gap-2 mt-1.5">
            {CATS.map(([k, l]) => (
              <button
                key={k}
                data-testid={`bh-cat-${k}`}
                onClick={() => toggle("categories", k)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  params.categories.includes(k)
                    ? "bg-amber-500 text-slate-950 border-amber-500"
                    : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {familles.length > 0 && (
          <div>
            <Label className="text-slate-500 text-xs">
              Familles prioritaires
            </Label>

            <div className="flex flex-wrap gap-2 mt-1.5">
              {familles.map((f) => (
                <button
                  key={f}
                  data-testid={`bh-fam-${f}`}
                  onClick={() => toggle("familles_prioritaires", f)}
                  className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${
                    params.familles_prioritaires.includes(f)
                      ? "bg-sky-50 text-sky-700 border-sky-200"
                      : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-xs text-slate-400">
            Hunter analyse les données publiques disponibles et ne confirme
            jamais un projet sans source.
          </div>

          <Button
            data-testid="bh-launch"
            onClick={launch}
            disabled={loading}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold shadow-sm"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Chasse en cours…
              </>
            ) : (
              <>
                <Radar className="h-4 w-4 mr-1" />
                Lancer la chasse
              </>
            )}
          </Button>

          {result?.prospects?.length > 0 && (
            <Button
              data-testid="bh-bulk-enrich"
              onClick={
                bulkEnriching
                  ? () => {
                      bulkStopRef.current = true;
                      setBulkStopRequested(true);
                    }
                  : enrichWholeHunt
              }
              variant="outline"
              className="font-bold"
            >
              {bulkEnriching ? (
                <>
                  <StopCircle className="h-4 w-4 mr-1" />
                  Arrêter ({bulkProgress.done}/{bulkProgress.total})
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-1" />
                  Enrichir toute la chasse
                </>
              )}
            </Button>
          )}
        </div>

        {bulkEnriching && (
          <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-3 text-xs text-violet-900">
            <div className="font-semibold mb-2">
              ⚡ Enrichissement Hunter — {bulkProgress.done}/{bulkProgress.total}
            </div>
            <div className="flex flex-wrap gap-3">
              <span>🟢 Direct : <b>{bulkStats.direct}</b></span>
              <span>🟡 Indirect : <b>{bulkStats.indirect}</b></span>
              <span>🔴 Aucun : <b>{bulkStats.none}</b></span>
              <span>⚠️ Erreurs : <b>{bulkStats.errors}</b></span>
            </div>
          </div>
        )}
      </div>

      {/* Résultats */}
      {result && (
        <div className="space-y-4" data-testid="bh-results">
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="rounded-xl bg-white border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Cibles</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">
                {result.count || 0}
              </div>
            </div>

            <div className="rounded-xl bg-white border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Entreprises analysées</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">
                {result.companies_scanned || 0}
              </div>
            </div>

            <div className="rounded-xl bg-white border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Localisées</div>
              <div className="text-2xl font-bold text-sky-600 mt-1">
                {result.localised_count || 0}
              </div>
            </div>

            <div className="rounded-xl bg-white border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Priorité haute</div>
              <div className="text-2xl font-bold text-red-500 mt-1">
                {result.par_priorite?.HAUTE || 0}
              </div>
            </div>

            <div className="rounded-xl bg-white border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Pages analysées</div>
              <div className="text-2xl font-bold text-amber-600 mt-1">
                {result.pages_scanned || 0}
              </div>
            </div>
          </div>

          {/* Barre vue */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex flex-wrap gap-2 text-sm">
              {Object.entries(result.par_priorite || {}).map(
                ([k, v]) =>
                  v > 0 && (
                    <button
                      key={k}
                      onClick={() =>
                        setMapFilter((f) => (f === k ? "ALL" : k))
                      }
                      className={`text-[11px] font-bold px-2 py-1 rounded border ${
                        PRIO[k]?.c || ""
                      } ${
                        mapFilter === k
                          ? "ring-2 ring-offset-1 ring-slate-200"
                          : ""
                      }`}
                    >
                      {PRIO[k]?.l || k}: {v}
                    </button>
                  )
              )}
            </div>

            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
              <button
                onClick={() => setViewMode("list")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${
                  viewMode === "list"
                    ? "bg-slate-900 text-white"
                    : "text-slate-500"
                }`}
              >
                <List className="h-3.5 w-3.5" />
                Liste
              </button>

              <button
                onClick={() => setViewMode("map")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${
                  viewMode === "map"
                    ? "bg-slate-900 text-white"
                    : "text-slate-500"
                }`}
              >
                <Map className="h-3.5 w-3.5" />
                Carte
              </button>
            </div>
          </div>

          {result.count === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-500 text-sm">
              Aucun prospect dans cette zone/critères. Élargissez le rayon ou
              les catégories.
            </div>
          )}

          {/* CARTE */}
          {viewMode === "map" && result.count > 0 && (
            <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
              <div className="px-5 py-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    <Navigation className="h-4 w-4 text-sky-500" />
                    Prospection terrain
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {localisedProspects.length} cible(s) avec position
                    exploitable
                  </div>
                </div>

                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span>● Prioritaire</span>
                  <span>● Intéressant</span>
                  <span>● Surveillance</span>
                </div>
              </div>

              <div className="h-[620px] relative">
                <MapContainer
                  center={STORE_CENTER}
                  zoom={10}
                  scrollWheelZoom
                  className="h-full w-full z-0"
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  <Circle
                    center={STORE_CENTER}
                    radius={params.radius_km * 1000}
                    pathOptions={{
                      color: "#f59e0b",
                      fillColor: "#f59e0b",
                      fillOpacity: 0.04,
                      weight: 1,
                    }}
                  />

                  {mapProspects
                    .filter(
                      (p) =>
                        p.latitude != null &&
                        p.longitude != null &&
                        p.distance_km != null
                    )
                    .map((p) => (
                      <Marker
                        key={p.siren}
                        position={[
                          Number(p.latitude),
                          Number(p.longitude),
                        ]}
                        icon={markerIcon(p.priorite)}
                        eventHandlers={{
                          click: () => setSel(p),
                        }}
                      >
                        <Popup>
                          <div className="min-w-[220px]">
                            <div className="font-bold text-slate-900">
                              {p.nom}
                            </div>

                            <div className="text-xs text-slate-500 mt-1">
                              {p.ville} · {p.distance_km} km
                            </div>

                            <div className="flex items-center gap-2 mt-2">
                              <b>{p.score}/100</b>
                              <span>{p.priorite}</span>
                            </div>

                            <div className="text-xs mt-2">
                              {p.profil_cible}
                            </div>

                            {p.offres_compatibles?.length > 0 && (
                              <div className="text-xs text-slate-500 mt-1">
                                {p.offres_compatibles.slice(0, 2).join(" · ")}
                              </div>
                            )}

                            <div className="flex gap-3 mt-3">
                              <button
                                className="text-xs font-semibold text-sky-600"
                                onClick={() => setSel(p)}
                              >
                                Voir la fiche →
                              </button>

                              <button
                                className="text-xs font-semibold text-amber-600"
                                onClick={() => toggleTour(p)}
                              >
                                {isInTour(p)
                                  ? "✓ Dans la tournée"
                                  : "+ Ajouter"}
                              </button>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                </MapContainer>

                <div className="absolute left-4 bottom-4 z-[1000] rounded-xl bg-white/95 backdrop-blur border border-slate-200 shadow-lg p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Zone de chasse
                  </div>
                  <div className="font-semibold text-slate-900 text-sm mt-0.5">
                    Bollène · rayon {params.radius_km} km
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {localisedProspects.length} positions fiables
                  </div>
                </div>

                {!hideFloatingTour && (
                  <div className="absolute right-4 top-4 z-[1000]">
                    <Button
                      variant="outline"
                      className="bg-white shadow-md border-slate-200 text-slate-700"
                      onClick={() => setTourOpen(true)}
                    >
                      <Route className="h-4 w-4 mr-1.5" />
                      Ma tournée
                      {tourItems.length > 0 && (
                        <span className="ml-1.5 rounded-full bg-slate-900 text-white px-1.5 py-0.5 text-[10px]">
                          {tourItems.length}
                        </span>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* LISTE */}
          {viewMode === "list" && result.count > 0 && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {mapProspects.map((p) => (
                <button
                  key={p.siren}
                  data-testid={`bh-result-${p.siren}`}
                  onClick={() => setSel(p)}
                  className="text-left rounded-xl bg-white border border-slate-200 p-4 hover:border-amber-400 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold truncate flex items-center gap-2 text-slate-900">
                        <Building2 className="h-4 w-4 text-amber-500 shrink-0" />
                        {p.nom}
                      </div>

                      <div className="text-xs text-slate-500 mt-1">
                        {p.categorie_label} · {p.ville} ·{" "}
                        {p.distance_km != null
                          ? `${p.distance_km} km`
                          : "Position inconnue"}
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <div
                        className="font-mono-metric text-xl font-bold text-amber-500"
                        data-testid={`bh-score-${p.siren}`}
                      >
                        {p.score}
                        <span className="text-xs text-slate-400">/100</span>
                      </div>

                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                          PRIO[p.priorite]?.c || ""
                        }`}
                      >
                        {PRIO[p.priorite]?.l || p.priorite}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="px-1.5 py-0.5 rounded border bg-slate-50 text-slate-500 border-slate-200">
                      {p.profil_cible || "Profil à qualifier"}
                    </span>

                    <span className="text-slate-500">
                      {p.offres_compatibles?.slice(0, 2).join(" · ") || "—"}
                    </span>

                    {inCrm(p) && (
                      <span className="inline-flex items-center gap-1 text-emerald-600">
                        <CheckCircle2 className="h-3 w-3" />
                        dans le CRM
                      </span>
                    )}

                    {p.dedup_status === "doublon_probable" && (
                      <span className="inline-flex items-center gap-1 text-amber-600">
                        <AlertTriangle className="h-3 w-3" />
                        doublon potentiel
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Ma tournée */}
      <Sheet open={tourOpen} onOpenChange={setTourOpen}>
        <SheetContent
          side="right"
          className="bg-white border-slate-200 text-slate-900 w-full sm:max-w-md overflow-y-auto"
          data-testid="bh-tour-sheet"
        >
          <SheetHeader>
            <SheetTitle className="font-head text-xl text-slate-900 flex items-center gap-2">
              <Route className="h-5 w-5 text-amber-500" />
              Ma tournée
            </SheetTitle>

            <SheetDescription>
              Préparez votre parcours de prospection avant de partir sur le terrain.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-5 space-y-5">

            {/* Nom + date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">
                  Nom de la tournée
                </Label>
                <Input
                  value={tourName}
                  onChange={(e) => setTourName(e.target.value)}
                  disabled={tourIsLocked}
                  className="mt-1 bg-slate-50 border-slate-200"
                  placeholder="Ex. Tournée artisans"
                />
              </div>

              <div>
                <Label className="text-xs text-slate-500">
                  Date
                </Label>
                <Input
                  type="date"
                  value={tourDate}
                  onChange={(e) => setTourDate(e.target.value)}
                  disabled={tourIsLocked}
                  className="mt-1 bg-slate-50 border-slate-200"
                />
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                <div className="text-[10px] uppercase font-bold text-slate-400">
                  Étapes
                </div>
                <div className="text-xl font-bold text-slate-900 mt-1">
                  {tourItems.length}
                </div>
              </div>

              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                <div className="text-[10px] uppercase font-bold text-slate-400">
                  Distance
                </div>
                <div className="text-xl font-bold text-slate-900 mt-1">
                  {tourDistance} km
                </div>
              </div>

              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                <div className="text-[10px] uppercase font-bold text-slate-400">
                  Départ
                </div>
                <div className="text-sm font-bold text-slate-900 mt-2">
                  Bollène
                </div>
              </div>
            </div>

            {/* Cycle de vie de la tournée */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                    Statut de la tournée
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={
                        "h-2.5 w-2.5 rounded-full " +
                        (
                          tourStatus === "En cours"
                            ? "bg-blue-500"
                            : tourStatus === "Terminée"
                              ? "bg-emerald-500"
                              : tourStatus === "Annulée"
                                ? "bg-red-500"
                                : "bg-amber-500"
                        )
                      }
                    />
                    <span className="font-bold text-slate-900">
                      {tourStatus}
                    </span>
                  </div>
                </div>

                {tourId && (
                  <span className="text-[10px] text-slate-400">
                    Tournée enregistrée
                  </span>
                )}
              </div>

              {tourStatus === "Planifiée" && (
                <div className="mt-3 flex gap-2">
                  <Button
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    onClick={() => updateTourStatus("En cours")}
                    disabled={updatingTourStatus || !tourItems.length}
                  >
                    {updatingTourStatus ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Navigation className="h-4 w-4 mr-1" />
                    )}
                    Démarrer
                  </Button>

                  <Button
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => updateTourStatus("Annulée")}
                    disabled={updatingTourStatus}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Annuler
                  </Button>
                </div>
              )}

              {tourStatus === "En cours" && (
                <div className="mt-3 flex gap-2">
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                    onClick={() => updateTourStatus("Terminée")}
                    disabled={updatingTourStatus}
                  >
                    {updatingTourStatus ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                    )}
                    Terminer la tournée
                  </Button>
                </div>
              )}

              {tourStatus === "Terminée" && (
                <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                  <div className="text-sm font-semibold text-emerald-700">
                    🏁 Tournée terminée
                  </div>
                  <div className="text-xs text-emerald-600 mt-1">
                    {tourItems.filter((p) => p.statut === "Visitée").length} visite(s)
                    {" · "}
                    {tourItems.filter((p) => p.statut === "Absent").length} absent(s)
                    {" · "}
                    {tourItems.filter((p) => p.statut === "À revoir").length} à revoir
                  </div>
                </div>
              )}

              {tourStatus === "Annulée" && (
                <div className="mt-3 rounded-lg bg-red-50 border border-red-100 p-3">
                  <div className="text-sm font-semibold text-red-700">
                    Tournée annulée
                  </div>
                  <div className="text-xs text-red-600 mt-1">
                    Cette tournée est clôturée et ne peut plus être modifiée.
                  </div>
                </div>
              )}
            </div>

            {/* Liste */}
            {tourItems.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
                <Route className="h-8 w-8 mx-auto text-slate-300" />

                <div className="text-sm font-semibold text-slate-600 mt-2">
                  Aucune étape
                </div>

                <p className="text-xs text-slate-400 mt-1">
                  Ajoutez des prospects depuis la carte ou leur fiche.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Ordre de passage
                  </div>

                  <span className="text-[11px] text-slate-400">
                    {tourItems.length} prospect(s)
                  </span>
                </div>

                {tourItems.map((p, index) => (
                  <div
                    key={p.siren}
                    className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
                  >
                    <div className="flex items-start gap-2">

                      <div className="mt-1 text-slate-300">
                        <GripVertical className="h-4 w-4" />
                      </div>

                      <div className="h-7 w-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {index + 1}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm text-slate-900 truncate">
                          {p.nom}
                        </div>

                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {p.ville}
                          {p.distance_km != null
                            ? ` · ${p.distance_km} km`
                            : ""}
                        </div>

                        <div className="flex flex-wrap gap-1 mt-1.5">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 border border-slate-100 text-slate-500">
                            {p.priorite}
                          </span>

                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 border border-sky-100 text-sky-600">
                            {p.score}/100
                          </span>
                        </div>
                      </div>

                      {/* Contrôles ordre */}
                      <div className="flex flex-col gap-0.5">
                        <button
                          title="Monter"
                          disabled={tourIsLocked || index === 0}
                          onClick={() => moveTourItem(index, -1)}
                          className="p-1 rounded hover:bg-slate-100 disabled:opacity-20"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>

                        <button
                          title="Descendre"
                          disabled={tourIsLocked || index === tourItems.length - 1}
                          onClick={() => moveTourItem(index, 1)}
                          className="p-1 rounded hover:bg-slate-100 disabled:opacity-20"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>

                        <button
                          title="Retirer"
                          onClick={() => removeTourItem(p.siren)}
                          disabled={tourIsLocked}
                          className="p-1 rounded hover:bg-red-50 text-red-400"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>

                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Résumé */}
            <div className="rounded-xl bg-slate-900 text-white p-4">
              <div className="flex items-center gap-2 text-amber-400">
                <Navigation className="h-4 w-4" />
                <span className="text-sm font-semibold">
                  Parcours actuel
                </span>
              </div>

              <p className="text-xs text-slate-300 mt-2">
                Départ Bollène → {tourItems.length} étape(s).
                Distance estimée : {tourDistance} km.
              </p>

              <p className="text-[10px] text-slate-400 mt-2">
                Estimation géographique, avant optimisation routière.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 border-slate-200"
                onClick={() => setTourItems([])}
                disabled={!tourItems.length || tourIsLocked}
              >
                Vider
              </Button>

              <Button
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
                onClick={saveTour}
                disabled={!tourItems.length || savingTour || tourIsLocked}
              >
                {savingTour ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Enregistrement…
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-1" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>

          </div>
        </SheetContent>
      </Sheet>

      {/* Fiche prospect */}
      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent
          side="right"
          className="bg-white border-slate-200 text-slate-900 w-full sm:max-w-lg overflow-y-auto"
          data-testid="bh-detail-sheet"
        >
          {sel && (
            <>
              <SheetHeader>
                <SheetTitle className="font-head text-lg text-slate-900 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-amber-500" />
                  {sel.nom}
                </SheetTitle>

                <SheetDescription>
                  Intelligence commerciale Hunter et prochaine action.
                </SheetDescription>
              </SheetHeader>

              <div className="mt-5 space-y-4">
                {/* Identité */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                    {sel.categorie_label}
                  </span>

                  <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {sel.ville}{" "}
                    {sel.code_postal !== "Non renseigné"
                      ? `(${sel.code_postal})`
                      : ""}{" "}
                    ·{" "}
                    {sel.distance_km != null
                      ? `${sel.distance_km} km`
                      : "Distance inconnue"}
                  </span>
                </div>

                {/* Score */}
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-slate-500">
                        Potentiel commercial
                      </div>

                      <span className="font-mono-metric text-3xl font-bold text-amber-500">
                        {sel.score}
                        <span className="text-sm text-slate-400">/100</span>
                      </span>
                    </div>

                    <span
                      className={`text-[10px] font-bold px-2 py-1 rounded border ${
                        PRIO[sel.priorite]?.c || ""
                      }`}
                    >
                      {sel.niveau_cible ||
                        PRIO[sel.priorite]?.l ||
                        sel.priorite}
                    </span>
                  </div>

                  <div className="mt-4 space-y-2">
                    {Object.entries(SCORE_LABELS).map(
                      ([k, [lab, max]]) => (
                        <div key={k} className="flex items-center gap-2">
                          <span className="text-xs text-slate-500 w-40">
                            {lab}
                          </span>

                          <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                            <div
                              className="h-full bg-amber-500"
                              style={{
                                width: `${Math.min(
                                  100,
                                  ((sel.score_detail?.[k] || 0) / max) *
                                    100
                                )}%`,
                              }}
                            />
                          </div>

                          <span className="font-mono-metric text-xs text-slate-600 w-12 text-right">
                            {sel.score_detail?.[k] || 0}/{max}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* Intelligence */}
                <div className="rounded-xl bg-white border border-slate-200 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    <b className="text-sm text-slate-900">
                      Intelligence commerciale
                    </b>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs px-2 py-1 rounded-lg bg-sky-50 text-sky-700 border border-sky-100">
                      {sel.profil_cible || sel.orientation_label || "Profil"}
                    </span>

                    {sel.score_strategique != null && (
                      <span className="text-xs px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100">
                        Stratégique {sel.score_strategique}/100
                      </span>
                    )}
                  </div>

                  {sel.raisons_priorite?.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-700">
                        Pourquoi cette cible ?
                      </div>

                      <ul className="mt-1.5 space-y-1">
                        {sel.raisons_priorite.map((r, i) => (
                          <li
                            key={i}
                            className="text-xs text-slate-500 flex gap-1.5"
                          >
                            <span className="text-amber-500">•</span>
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {sel.signaux_commerciaux?.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-700">
                        Signaux
                      </div>

                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {sel.signaux_commerciaux.map((s, i) => (
                          <span
                            key={i}
                            className="text-[11px] px-2 py-1 rounded-lg bg-slate-50 text-slate-500 border border-slate-100"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Offre */}
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Target className="h-4 w-4 text-sky-500" />
                    Angle commercial
                  </div>

                  <div className="text-xs text-slate-500">
                    Offres compatibles :
                    <span className="font-semibold text-slate-700 ml-1">
                      {sel.offres_compatibles?.length
                        ? sel.offres_compatibles.join(" · ")
                        : "À qualifier"}
                    </span>
                  </div>

                  {sel.argument_commercial && (
                    <p className="text-xs text-slate-600">
                      {sel.argument_commercial}
                    </p>
                  )}
                </div>

                {/* Opportunité Pipeline */}
                <div
                  className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 space-y-3"
                  data-testid="bh-opportunity-panel"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <BriefcaseBusiness className="h-4 w-4 text-blue-600" />
                        <b className="text-sm text-slate-900">
                          Opportunité commerciale
                        </b>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        Transformer cette cible Hunter en opportunité suivie
                        dans le Pipeline.
                      </div>
                    </div>

                    {loadingOpportunity ? (
                      <Button size="sm" variant="outline" disabled>
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        Vérification…
                      </Button>
                    ) : createdOpportunity || existingOpportunity ? (
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => {
                          const opportunity =
                            createdOpportunity || existingOpportunity;

                          window.location.href =
                            `/pipeline?opportunity=${opportunity.id}`;
                        }}
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1" />
                        Voir dans le Pipeline
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={openOpportunityDialog}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Créer
                      </Button>
                    )}
                  </div>

                  {(createdOpportunity || existingOpportunity) && (
                    <div className="rounded-lg bg-white border border-blue-100 px-3 py-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        <div className="text-xs font-semibold text-slate-800">
                          {createdOpportunity
                            ? "Opportunité créée"
                            : "Opportunité en cours"}
                        </div>
                      </div>

                      <div className="text-xs font-semibold text-slate-700 mt-1">
                        {(createdOpportunity || existingOpportunity).titre}
                      </div>

                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px] text-slate-500">
                        <span>
                          Étape :{" "}
                          <b className="text-slate-700">
                            {(createdOpportunity || existingOpportunity)
                              .etape || "Prospection"}
                          </b>
                        </span>

                        <span>
                          Valeur :{" "}
                          <b className="text-slate-700">
                            {Number(
                              (createdOpportunity || existingOpportunity)
                                .valeur_ht || 0
                            ).toLocaleString("fr-FR")}{" "}
                            € HT
                          </b>
                        </span>

                        {(createdOpportunity || existingOpportunity)
                          .famille_principale && (
                          <span>
                            Famille :{" "}
                            <b className="text-slate-700">
                              {(createdOpportunity || existingOpportunity)
                                .famille_principale}
                            </b>
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Coordonnées professionnelles */}
                <div
                  className="rounded-xl border border-slate-200 bg-white p-4 space-y-3"
                  data-testid="bh-enrichment-panel"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-sky-500" />
                        <b className="text-sm text-slate-900">
                          Coordonnées professionnelles
                        </b>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        Données publiques ou confirmées dans le CRM.
                      </div>
                    </div>

                    <Button
                      size="sm"
                      variant="outline"
                      disabled={enriching}
                      className="border-sky-200 text-sky-700 hover:bg-sky-50"
                      onClick={enrichProspect}
                    >
                      {enriching ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          Recherche…
                        </>
                      ) : (
                        <>
                          <Radar className="h-3.5 w-3.5 mr-1" />
                          {sel.hunter?.enrichissement?.derniere_tentative
                            ? "Réenrichir"
                            : "Enrichir"}
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={webEnriching}
                      className="border-violet-200 text-violet-700 hover:bg-violet-50"
                      onClick={enrichProspectWeb}
                    >
                      {webEnriching ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          Web…
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-3.5 w-3.5 mr-1" />
                          Recherche Web
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="grid gap-2">
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">
                        Téléphone
                      </div>
                      {sel.telephone ? (
                        <a
                          href={`tel:${sel.telephone}`}
                          className="text-sm font-semibold text-sky-700 hover:underline"
                        >
                          {sel.telephone}
                        </a>
                      ) : (
                        <div className="text-sm text-slate-400">
                          Non renseigné
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">
                        Email
                      </div>
                      {sel.email ? (
                        <a
                          href={`mailto:${sel.email}`}
                          className="text-sm font-semibold text-sky-700 hover:underline break-all"
                        >
                          {sel.email}
                        </a>
                      ) : (
                        <div className="text-sm text-slate-400">
                          Non renseigné
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">
                        Site web
                      </div>
                      {sel.site_web ? (
                        <a
                          href={
                            sel.site_web.startsWith("http")
                              ? sel.site_web
                              : `https://${sel.site_web}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-semibold text-sky-700 hover:underline break-all"
                        >
                          {sel.site_web}
                        </a>
                      ) : (
                        <div className="text-sm text-slate-400">
                          Non renseigné
                        </div>
                      )}
                    </div>
                  </div>

                  {sel.hunter?.enrichissement?.derniere_tentative && (
                    <div className="text-[10px] text-slate-400">
                      Dernière recherche :{" "}
                      {new Date(
                        sel.hunter.enrichissement.derniere_tentative
                      ).toLocaleString("fr-FR")}
                    </div>
                  )}

                  {(enrichResult?.status === "aucune_coordonnée_trouvee" ||
                    sel.hunter?.enrichissement?.statut ===
                      "aucune_coordonnée_trouvee") && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                      Aucune coordonnée publique exploitable trouvée.
                      <div className="text-[10px] mt-0.5 text-amber-600">
                        Le CRM n'invente aucune donnée.
                      </div>
                    </div>
                  )}
                </div>

                {webEnrichResult && webEnrichResult.status !== "provider_non_configure" && (
                  <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-violet-900">
                      <Sparkles className="h-4 w-4" />
                      Recherche Web approfondie
                    </div>
                    <div className="text-xs text-violet-800">{webEnrichResult.message}</div>
                    {webEnrichResult.source && (
                      <a href={webEnrichResult.source} target="_blank" rel="noreferrer" className="text-xs font-semibold text-violet-700 hover:underline inline-flex items-center gap-1">
                        Source vérifiée <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {webEnrichResult.matching_score > 0 && (
                      <div className="text-[11px] text-slate-600">
                        Concordance identité : <b>{webEnrichResult.matching_score}/100</b>
                        {webEnrichResult.matching_reason?.length ? ` · ${webEnrichResult.matching_reason.join(" · ")}` : ""}
                      </div>
                    )}
                    {!!webEnrichResult.candidates?.length && (
                      <div className="space-y-2 pt-1">
                        <div className="text-[10px] font-semibold text-slate-600">
                          {webEnrichResult.candidates.length} source(s) analysée(s) — les coordonnées ne sont retenues qu’après concordance avec l’entreprise.
                        </div>
                        <div className="space-y-1.5">
                          {webEnrichResult.candidates.map((candidate, idx) => {
                            const accepted = candidate.accepted;
                            const score = Number(candidate.final_score ?? candidate.score ?? 0);
                            return (
                              <div key={`${candidate.url || "source"}-${idx}`} className="rounded-lg border border-slate-200 bg-white/80 p-2">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <div className="text-[11px] font-semibold text-slate-800 truncate">
                                      {candidate.title || candidate.url || "Source Web"}
                                    </div>
                                    {candidate.url && (
                                      <a href={candidate.url} target="_blank" rel="noreferrer" className="text-[10px] text-violet-700 hover:underline break-all">
                                        {candidate.url} <ExternalLink className="inline h-3 w-3" />
                                      </a>
                                    )}
                                  </div>
                                  <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${accepted ? "bg-emerald-100 text-emerald-700" : score >= 50 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                                    {accepted ? "RETENU" : "REJETÉ"} · {score}/100
                                  </span>
                                </div>
                                <div className="mt-1 text-[10px] text-slate-500">
                                  Recherche : <b>{Number(candidate.search_score ?? score)}/100</b>
                                  {candidate.verification_score != null && <> · Vérification : <b>{Number(candidate.verification_score)}/100</b></>}
                                  <> · Final : <b>{score}/100</b></>
                                </div>
                                {!!candidate.matching_reason?.length && (
                                  <div className="mt-1 text-[10px] text-slate-500">
                                    Concordance recherche : {candidate.matching_reason.join(" · ")}
                                  </div>
                                )}
                                {!!candidate.verification_reasons?.length && (
                                  <div className="mt-1 text-[10px] text-slate-500">
                                    Concordance page : {candidate.verification_reasons.join(" · ")}
                                  </div>
                                )}
                                {candidate.decision_reason && (
                                  <div className={`mt-1 text-[10px] ${accepted ? "text-emerald-700" : "text-amber-700"}`}>
                                    {candidate.decision_reason}
                                  </div>
                                )}
                                {!!candidate.contacts && Object.entries(candidate.contacts).filter(([key, value]) => !key.endsWith("_candidates") && value).length > 0 && (
                                  <div className="mt-1 text-[10px] text-slate-600">
                                    Coordonnées candidates : {Object.entries(candidate.contacts).filter(([key, value]) => !key.endsWith("_candidates") && value).map(([key, value]) => `${key}=${value}`).join(" · ")}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Création opportunité */}
      <Sheet open={opportunityOpen} onOpenChange={setOpportunityOpen}>
        <SheetContent
          side="right"
          className="bg-white border-slate-200 text-slate-900 w-full sm:max-w-lg overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle className="font-head text-lg text-slate-900 flex items-center gap-2">
              <BriefcaseBusiness className="h-5 w-5 text-blue-600" />
              Créer une opportunité
            </SheetTitle>
            <SheetDescription>
              Transformer {sel?.nom || "ce prospect"} en opportunité Pipeline.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-5 space-y-4">
            <div>
              <Label>Titre</Label>
              <Input
                className="mt-1"
                value={opportunityForm.titre}
                onChange={(e) =>
                  setOpportunityForm((f) => ({
                    ...f,
                    titre: e.target.value,
                  }))
                }
              />
            </div>

            <div>
              <Label>Famille principale</Label>
              <Select
                value={opportunityForm.famille_principale || undefined}
                onValueChange={(value) =>
                  setOpportunityForm((f) => ({
                    ...f,
                    famille_principale: value,
                  }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choisir une famille…" />
                </SelectTrigger>
                <SelectContent>
                  {(familles.length
                    ? familles
                    : sel?.familles_compatibles ||
                      sel?.hunter?.familles_compatibles ||
                      []
                  ).map((famille) => (
                    <SelectItem key={famille} value={famille}>
                      {famille}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Valeur estimée HT</Label>
                <div className="relative mt-1">
                  <CircleDollarSign className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    className="pl-9"
                    value={opportunityForm.valeur_ht}
                    onChange={(e) =>
                      setOpportunityForm((f) => ({
                        ...f,
                        valeur_ht: e.target.value,
                      }))
                    }
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <Label>Marge estimée HT</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1"
                  value={opportunityForm.marge_estimee_ht}
                  onChange={(e) =>
                    setOpportunityForm((f) => ({
                      ...f,
                      marge_estimee_ht: e.target.value,
                    }))
                  }
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <Label>Priorité</Label>
              <Select
                value={opportunityForm.priorite}
                onValueChange={(value) =>
                  setOpportunityForm((f) => ({
                    ...f,
                    priorite: value,
                  }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normale">Normale</SelectItem>
                  <SelectItem value="Haute">Haute</SelectItem>
                  <SelectItem value="Basse">Basse</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Prochaine action</Label>
              <Input
                className="mt-1"
                value={opportunityForm.prochaine_action}
                onChange={(e) =>
                  setOpportunityForm((f) => ({
                    ...f,
                    prochaine_action: e.target.value,
                  }))
                }
                placeholder="Prendre contact"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date prochaine action</Label>
                <Input
                  type="date"
                  className="mt-1"
                  value={opportunityForm.prochaine_date}
                  onChange={(e) =>
                    setOpportunityForm((f) => ({
                      ...f,
                      prochaine_date: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <Label>Échéance opportunité</Label>
                <Input
                  type="date"
                  className="mt-1"
                  value={opportunityForm.echeance}
                  onChange={(e) =>
                    setOpportunityForm((f) => ({
                      ...f,
                      echeance: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div>
              <Label>Commercial</Label>
              <Input
                className="mt-1"
                value={opportunityForm.commercial}
                onChange={(e) =>
                  setOpportunityForm((f) => ({
                    ...f,
                    commercial: e.target.value,
                  }))
                }
                placeholder="Optionnel"
              />
            </div>

            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-500">
              <b className="text-slate-700">Hunter</b> · Score{" "}
              {sel?.score_strategique ?? sel?.score ?? 0}/100 ·{" "}
              {sel?.profil_cible || sel?.orientation_label || "Cible commerciale"}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setOpportunityOpen(false)}
                disabled={creatingOpportunity}
              >
                Annuler
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={createOpportunity}
                disabled={creatingOpportunity || !opportunityForm.titre.trim()}
              >
                {creatingOpportunity ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Création…
                  </>
                ) : (
                  <>
                    <BriefcaseBusiness className="h-4 w-4 mr-2" />
                    Créer dans le Pipeline
                  </>
                )}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Contact & prospection */}
                <div
                  className="rounded-xl border border-slate-200 bg-white p-4 space-y-3"
                  data-testid="bh-contact-panel"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-sky-500" />
                        <b className="text-sm text-slate-900">
                          Prospection
                        </b>
                      </div>

                      <div className="text-xs text-slate-500 mt-1">
                        Statut :
                        <span className="font-semibold text-slate-700 ml-1">
                          {sel.prospection?.statut ||
                            sel.statut_prospect ||
                            "À contacter"}
                        </span>
                      </div>
                    </div>

                    {sel.prospection?.nombre_contacts > 0 && (
                      <span className="text-[10px] px-2 py-1 rounded-lg bg-slate-50 border border-slate-200 text-slate-500">
                        {sel.prospection.nombre_contacts} contact(s)
                      </span>
                    )}
                  </div>

                  {/* Dernier contact */}
                  {sel.prospection?.dernier_contact && (
                    <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                      <div>
                        Dernier contact :{" "}
                        <b>
                          {new Date(
                            sel.prospection.dernier_contact
                          ).toLocaleDateString("fr-FR")}
                        </b>
                      </div>

                      {sel.prospection.dernier_canal && (
                        <div className="mt-0.5">
                          Canal :{" "}
                          <b>{sel.prospection.dernier_canal}</b>
                        </div>
                      )}

                      {sel.prospection.dernier_resultat && (
                        <div className="mt-0.5">
                          Résultat :{" "}
                          <b>{sel.prospection.dernier_resultat}</b>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Opposition */}
                  {sel.prospection?.opposition ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-red-700">
                        <AlertTriangle className="h-4 w-4" />
                        Opposition enregistrée
                      </div>

                      <p className="text-xs text-red-600 mt-1">
                        Cette entreprise ne doit plus être prospectée.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-sky-200 text-sky-700 hover:bg-sky-50"
                          onClick={() => {
                            setContactCanal("Téléphone");
                            setContactResultat("");
                            setContactCommentaire("");
                            setContactNextDate("");
                            setContactNextAction("");
                            setContactOpen(true);
                          }}
                        >
                          <Phone className="h-4 w-4 mr-1.5" />
                          Appeler
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          className="border-sky-200 text-sky-700 hover:bg-sky-50"
                          onClick={() => {
                            setContactCanal("Email");
                            setContactResultat("");
                            setContactCommentaire("");
                            setContactNextDate("");
                            setContactNextAction("");
                            setContactOpen(true);
                          }}
                        >
                          <ExternalLink className="h-4 w-4 mr-1.5" />
                          Mail
                        </Button>
                      </div>

                      <Button
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                        onClick={() => {
                          setContactCanal("Téléphone");
                          setContactResultat("");
                          setContactCommentaire("");
                          setContactNextDate("");
                          setContactNextAction("");
                          setContactOpen(true);
                        }}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1.5" />
                        Enregistrer un contact
                      </Button>
                    </>
                  )}
                </div>

                {/* Formulaire contact */}
                <Sheet
                  open={contactOpen}
                  onOpenChange={setContactOpen}
                >
                  <SheetContent
                    side="right"
                    className="bg-white border-slate-200 text-slate-900 w-full sm:max-w-md overflow-y-auto"
                  >
                    <SheetHeader>
                      <SheetTitle className="font-head text-xl">
                        Enregistrer un contact
                      </SheetTitle>

                      <SheetDescription>
                        {sel.nom}
                      </SheetDescription>
                    </SheetHeader>

                    <div className="mt-5 space-y-5">

                      <div>
                        <Label className="text-xs text-slate-500">
                          Canal
                        </Label>

                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {["Téléphone", "Email", "Visite", "Autre"].map(
                            (canal) => (
                              <Button
                                key={canal}
                                type="button"
                                variant={
                                  contactCanal === canal
                                    ? "default"
                                    : "outline"
                                }
                                onClick={() =>
                                  setContactCanal(canal)
                                }
                                className={
                                  contactCanal === canal
                                    ? "bg-slate-900 text-white"
                                    : "border-slate-200"
                                }
                              >
                                {canal}
                              </Button>
                            )
                          )}
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs text-slate-500">
                          Résultat
                        </Label>

                        <div className="grid grid-cols-1 gap-2 mt-2">
                          {[
                            "Intéressé",
                            "Demande de rappel",
                            "RDV prévu",
                            "Pas intéressé",
                            "Message laissé",
                            "Pas de réponse",
                            "Opposition",
                          ].map((resultat) => (
                            <Button
                              key={resultat}
                              type="button"
                              variant={
                                contactResultat === resultat
                                  ? "default"
                                  : "outline"
                              }
                              onClick={() =>
                                setContactResultat(resultat)
                              }
                              className={`justify-start ${
                                contactResultat === resultat
                                  ? "bg-slate-900 text-white"
                                  : resultat === "Opposition"
                                  ? "border-red-200 text-red-600 hover:bg-red-50"
                                  : "border-slate-200"
                              }`}
                            >
                              {resultat}
                            </Button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs text-slate-500">
                          Commentaire
                        </Label>

                        <textarea
                          value={contactCommentaire}
                          onChange={(e) =>
                            setContactCommentaire(e.target.value)
                          }
                          rows={4}
                          placeholder="Compte-rendu de l'échange..."
                          className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-200"
                        />
                      </div>

                      {[
                        "Demande de rappel",
                        "RDV prévu",
                      ].includes(contactResultat) && (
                        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 space-y-3">
                          <div className="text-sm font-semibold text-amber-800">
                            Prochaine action
                          </div>

                          <div>
                            <Label className="text-xs text-slate-500">
                              Action
                            </Label>

                            <Input
                              value={contactNextAction}
                              onChange={(e) =>
                                setContactNextAction(e.target.value)
                              }
                              placeholder={
                                contactResultat === "RDV prévu"
                                  ? "Rendez-vous commercial"
                                  : "Rappeler le prospect"
                              }
                              className="mt-1 bg-white border-amber-200"
                            />
                          </div>

                          <div>
                            <Label className="text-xs text-slate-500">
                              Date
                            </Label>

                            <Input
                              type="date"
                              value={contactNextDate}
                              onChange={(e) =>
                                setContactNextDate(e.target.value)
                              }
                              className="mt-1 bg-white border-amber-200"
                            />
                          </div>
                        </div>
                      )}

                      <Button
                        className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                        disabled={
                          !contactResultat || savingContact
                        }
                        onClick={saveContact}
                      >
                        {savingContact ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Enregistrement…
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Valider le contact
                          </>
                        )}
                      </Button>

                    </div>
                  </SheetContent>
                </Sheet>

                {/* Action recommandée */}
                <div className="rounded-xl bg-slate-900 p-4 text-white">
                  <div className="flex items-center gap-2 text-amber-400">
                    <Crosshair className="h-4 w-4" />
                    <b className="text-sm">Prochaine action</b>
                  </div>

                  <div className="text-sm font-semibold mt-2">
                    {sel.action_recommandee?.label ||
                      "Qualifier le prospect"}
                  </div>

                  <p className="text-xs text-slate-300 mt-1">
                    {sel.justification}
                  </p>
                </div>

                {/* Signal */}
                <div className="rounded-xl border border-slate-200 p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-semibold text-slate-800">
                      Signal détecté
                    </span>

                    <span
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                        sel.signal_niveau === "probable"
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-slate-50 text-slate-500 border-slate-200"
                      }`}
                    >
                      {sel.signal_niveau}
                    </span>
                  </div>

                  <p className="text-xs text-slate-500">
                    {sel.signal} · {sel.signal_date}
                  </p>
                </div>

                {/* Source */}
                <div className="text-xs text-slate-500 space-y-2">
                  <div>
                    SIREN :{" "}
                    <span className="font-mono text-slate-700">
                      {sel.siren || "Non renseigné"}
                    </span>
                  </div>

                  <div>
                    Établissements :{" "}
                    <span className="font-semibold text-slate-700">
                      {sel.nombre_etablissements || 1}
                    </span>
                  </div>

                  <a
                    href={sel.source_info}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sky-600 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Vérifier la source
                  </a>
                </div>

                {/* Tournée terrain */}
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        <Route className="h-4 w-4 text-amber-500" />
                        Prospection terrain
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Ajouter ce prospect à votre tournée.
                      </div>
                    </div>

                    <Button
                      size="sm"
                      onClick={() => toggleTour(sel)}
                      variant={isInTour(sel) ? "outline" : "default"}
                      className={
                        isInTour(sel)
                          ? "bg-white border-amber-300 text-amber-700"
                          : "bg-amber-500 hover:bg-amber-600 text-slate-950"
                      }
                    >
                      {isInTour(sel) ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Sélectionné
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-1" />
                          Ajouter
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Actions CRM */}
                {inCrm(sel) ? (
                  <div className="space-y-2 pt-2">
                    <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold">
                      <CheckCircle2 className="h-4 w-4" />
                      Présent dans le CRM
                    </div>

                    {added[sel.siren] && (
                      <Select
                        onValueChange={(v) =>
                          api
                            .patch(
                              `/hunter/prospects/${added[sel.siren]}/statut`,
                              { statut_prospect: v }
                            )
                            .then(() => toast.success(`Statut : ${v}`))
                            .catch(() => toast.error("Erreur"))
                        }
                      >
                        <SelectTrigger
                          data-testid="bh-statut-select"
                          className="bg-white border-slate-200 h-9 text-xs"
                        >
                          <SelectValue placeholder="Faire évoluer le statut…" />
                        </SelectTrigger>

                        <SelectContent>
                          {STATUTS.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    <Button
                      data-testid="bh-log-action"
                      onClick={() => logAction(sel)}
                      variant="outline"
                      className="w-full border-slate-200 text-slate-700"
                    >
                      <Phone className="h-4 w-4 mr-1" />
                      Enregistrer un contact
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2 pt-2">
                    {sel.dedup_status === "doublon_probable" && (
                      <div className="flex items-center gap-2 text-amber-600 text-xs">
                        <AlertTriangle className="h-4 w-4" />
                        Doublon potentiel : {sel.existing_ref?.nom}
                      </div>
                    )}

                    <Button
                      data-testid={`bh-add-${sel.siren}`}
                      onClick={() =>
                        addToCrm(
                          sel,
                          sel.dedup_status === "doublon_probable"
                        )
                      }
                      className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      {sel.dedup_status === "doublon_probable"
                        ? "Ajouter quand même au CRM"
                        : "Ajouter au CRM"}
                    </Button>

                    <p className="text-[11px] text-slate-400 flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      Aucune création ni contact automatique — le commercial
                      décide.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <ActionLogDialog
        open={actionOpen}
        onOpenChange={setActionOpen}
        context={actionCtx}
        onSaved={() =>
          window.dispatchEvent(new CustomEvent("crm:data-changed"))
        }
      />
    </div>
  );
}
